import { useState, useEffect, useRef, useCallback } from "react";
import fetchWithCredentials from "apiClient";
import { SearchBox } from "components/ui/SearchBox";
import { useAuth } from "context/AuthContext";
import Spinner from "components/ui/Spinner";
import { GlobalContactSearchResult } from "types/GlobalContactSearch";

interface GlobalContactSearchProps {
  onSelectContact: (condoId: number, contactId: number) => void;
}

function residentTypeLabel(
  type: string | undefined,
  locale: "es" | "en"
): string {
  if (type === "owner") return locale === "es" ? "Propietario" : "Owner";
  if (type === "tenant") return locale === "es" ? "Inquilino" : "Tenant";
  return locale === "es" ? "Propietario No Residente" : "Non-resident owner";
}

function formatDetailLine(
  row: GlobalContactSearchResult,
  locale: "es" | "en"
): string {
  if (row.kind === "resident") {
    let s = residentTypeLabel(row.residentType, locale);
    if (row.unitName) s += ` • ${row.unitName}`;
    if (row.isActive === false) {
      s += ` • ${locale === "es" ? "Inactivo" : "Inactive"}`;
    }
    return s;
  }
  return row.role || "";
}

export function GlobalContactSearch({
  onSelectContact,
}: GlobalContactSearchProps) {
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [results, setResults] = useState<GlobalContactSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (debouncedQuery.length < 2) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ q: debouncedQuery });
    fetchWithCredentials(`/manager-api/contacts/search/?${params.toString()}`)
      .then((data: { results?: GlobalContactSearchResult[] }) => {
        if (!cancelled) {
          setResults(data.results ?? []);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message);
          setResults([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const handleSelect = useCallback(
    (row: GlobalContactSearchResult) => {
      onSelectContact(row.condoId, row.contactId);
      setQuery("");
      setDebouncedQuery("");
      setResults([]);
      setOpen(false);
    },
    [onSelectContact]
  );

  const showPanel = open && debouncedQuery.length >= 2;

  const placeholder =
    locale === "es"
      ? "Buscar en todos los edificios..."
      : "Search all buildings...";

  return (
    <div ref={rootRef} className="mb-4 relative z-50">
      <SearchBox
        value={query}
        onChange={(v) => {
          setQuery(v);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
      />
      {query.trim().length > 0 && query.trim().length < 2 && (
        <p className="text-xs text-gray-500 mt-1">
          {locale === "es"
            ? "Escribe al menos 2 caracteres."
            : "Type at least 2 characters."}
        </p>
      )}
      {showPanel && (
        <div className="absolute left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded border border-gray-200 bg-white shadow-lg">
          {loading && (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          )}
          {!loading && error && (
            <div className="p-3 text-xs text-red-600">{error}</div>
          )}
          {!loading && !error && results.length === 0 && (
            <div className="p-3 text-xs text-gray-500">
              {locale === "es" ? "Sin resultados." : "No results."}
            </div>
          )}
          {!loading && !error && results.length > 0 && (
            <ul className="py-1">
              {results.map((row) => (
                <li key={`${row.kind}-${row.contactId}-${row.condoId}`}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(row)}
                  >
                    <div
                      className={`font-medium truncate ${
                        row.kind === "resident" && row.isActive === false
                          ? "text-gray-500"
                          : ""
                      }`}
                    >
                      {row.name}
                    </div>
                    <div className="text-gray-500 truncate">
                      <span className="text-gray-600">{row.condoName}</span>
                      {" • "}
                      {formatDetailLine(row, locale)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
